import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Download } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { format } from 'date-fns';
import api from '../../lib/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

export default function ClientReports() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/reports/clients');
      const result = response.data;
      setData(result);
    } catch (error) {
      toast.error('Failed to load report data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (!data || !data.clients) return;
    const headers = ['Name', 'Company', 'Email', 'Phone', 'Type', 'Industry', 'Created Date'];
    const rows = data.clients.map(c => [
      c.name || '',
      c.company_name || '',
      c.email || '',
      c.phone || '',
      c.type || '',
      c.industry || '',
      c.created_at ? format(new Date(c.created_at), 'yyyy-MM-dd') : ''
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `client-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    toast.success('Report exported to CSV');
  };

  if (loading) {
    return <div className="p-8"><div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-1/4"></div><div className="h-64 bg-gray-200 rounded"></div></div></div>;
  }

  const typeChartData = data?.summary?.type_breakdown ? Object.entries(data.summary.type_breakdown).map(([name, value]) => ({ name, value })) : [];
  const industryChartData = data?.summary?.industry_breakdown ? Object.entries(data.summary.industry_breakdown).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="p-8 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold tracking-tight">Client Reports</h1>
        <Button onClick={exportToCSV} variant="outline"><Download className="h-4 w-4 mr-2" />Export CSV</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardHeader><CardTitle>Total Clients</CardTitle></CardHeader><CardContent><div className="text-3xl font-bold">{data?.summary?.total_clients || 0}</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Client Types</CardTitle></CardHeader><CardContent><div className="text-lg">{Object.keys(data?.summary?.type_breakdown || {}).length} types</div></CardContent></Card>
        <Card><CardHeader><CardTitle>Industries</CardTitle></CardHeader><CardContent><div className="text-lg">{Object.keys(data?.summary?.industry_breakdown || {}).length} industries</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Clients by Type</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={typeChartData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">
                  {typeChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Clients by Industry</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={industryChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Client Details</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Company</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Industry</th>
                  <th className="text-left p-2">Created Date</th>
                </tr>
              </thead>
              <tbody>
                {data?.clients?.map((client, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="p-2">{client.name}</td>
                    <td className="p-2">{client.company_name || '-'}</td>
                    <td className="p-2">{client.email}</td>
                    <td className="p-2"><span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">{client.type || 'unknown'}</span></td>
                    <td className="p-2">{client.industry || 'unknown'}</td>
                    <td className="p-2">{client.created_at ? format(new Date(client.created_at), 'MMM dd, yyyy') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
